import { useSeo } from "../lib/seo";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { LoginCredentials, UserRole } from "../types/vtc";
import { AUTH_CONTENT, parseRole, safeNext, withNext } from "../data/auth";
import { login, saveSession } from "../lib/auth";
import { toast } from "../lib/toast";
import { ApiError, errorText } from "../lib/api";

import AuthLayout, { AuthHeading, authLink } from "../components/auth/AuthLayout";
import RoleTabs from "../components/auth/RoleTabs";
import {
  FormAlert,
  PasswordField,
  TextField,
} from "../components/ui/FormField";
import { emailRules } from "../components/auth/rules";
import { useT } from "../i18n";
import Button from "../components/ui/Button";

export default function Login() {
  const t = useT();
  useSeo({ title: t.seo.login, noindex: true }); // account pages stay out of search results
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = parseRole(searchParams.get("role"));
  const content = AUTH_CONTENT[role];
  const copy = t.auth.roles[role];
  const next = safeNext(searchParams.get("next"));

  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Omit<LoginCredentials, "role">>({
    defaultValues: { email: "", password: "", remember: true },
  });

  const selectRole = (next: UserRole) => {
    setSubmitError(null);
    const params: Record<string, string> = next === "driver" ? {} : { role: next };
    const back = safeNext(searchParams.get("next"));
    if (back) params.next = back;
    setSearchParams(params, { replace: true });
  };

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const session = await login({ ...values, role });
      saveSession(session, values.remember);
      toast.success(t.auth.toasts.loggedIn(session.name));
      navigate(next ?? content.dashboardHref);
    } catch (err) {
      // Account that never entered the signup code: the backend just e-mailed a new one.
      if (err instanceof ApiError && err.code === "EMAIL_NOT_VERIFIED") {
        toast.info(err.message);
        const params = new URLSearchParams({ email: values.email.trim() });
        if (role === "company") params.set("role", "company");
        if (next) params.set("next", next);
        return navigate(`/verify-email?${params}`);
      }
      const message = errorText(err);
      setSubmitError(message);
      toast.error(message);
    }
  });

  return (
    <AuthLayout activeHref="/login">
      <RoleTabs value={role} onChange={selectRole} />
      <AuthHeading title={copy.loginTitle} subtitle={copy.loginSubtitle} />

      <form onSubmit={onSubmit} noValidate className="grid gap-3.5">
        <TextField
          label={t.common.email}
          type="email"
          autoComplete="email"
          placeholder={t.common.emailPlaceholder}
          error={errors.email?.message}
          {...register("email", emailRules(t))}
        />

        <PasswordField
          label={t.common.password}
          placeholder={t.common.ph.password}
          autoComplete="current-password"
          error={errors.password?.message}
          {...register("password", {
            required: t.auth.login.passwordRequired,
            minLength: {
              value: 6,
              message: t.auth.login.passwordMin,
            },
          })}
        />

        <div className="flex items-center justify-between text-[13px]">
          <label className="flex cursor-pointer items-center gap-2 text-ink-soft">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand"
              {...register("remember")}
            />
            {t.auth.login.remember}
          </label>
          <Link
            to={
              role === "driver"
                ? "/forgot-password"
                : `/forgot-password?role=${role}`
            }
            className={authLink}
          >
            {t.auth.login.forgot}
          </Link>
        </div>

        {submitError && <FormAlert>{submitError}</FormAlert>}

        <Button
          type="submit"
          disabled={isSubmitting}
          className="mt-1.5 h-11 w-full cursor-pointer border-0"
        >
          {isSubmitting ? t.auth.login.submitting : t.common.login}
        </Button>
      </form>

      <p className="mt-6 mb-0 border-t border-line pt-5 text-center text-sm text-muted">
        {t.auth.login.noAccount}{" "}
        <Link to={withNext(content.signupHref, next)} className={`font-medium ${authLink}`}>
          {copy.signupLabel}
        </Link>
      </p>
    </AuthLayout>
  );
}
